/* eslint-disable */
// @ts-nocheck

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { Column } from '@tanstack/react-table'

import {
  ContainerQueryParams,
  useContainerQuery,
} from '@/hooks/container/useContainerQuery'
import { Container } from '@/interfaces/container'
import { storage } from '@/lib/utils'
import { useAppSelector } from '@/store/hooks'

import { ContainerFilters } from '../filters/hooks/useContainerFilters'
import { useContainerFiltersContext } from '../filters/hooks/ContainerFiltersProvider'

// Define a more complete interface for invoice data
interface ContainerData {
  items: Container[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the context type
interface TableContextType {
  currentPage: number
  setCurrentPage: (page: number) => void
  isFetching: boolean
  isError: boolean
  pageSize: number
  setPageSize: (size: number) => void
  containerData: ContainerData | null
  isLoading: boolean
  refetch: () => void
  // Add direct access to pagination methods for convenience
  goToNextPage: () => void
  goToPreviousPage: () => void
  // Skeleton data for loading state
  skeletonRowCount: number
  // Additional helpful properties
  hasNextPage: boolean
  hasPreviousPage: boolean
  // Filter management
  resetToFirstPage: () => void
  // Table columns for column selector
  columns: Column<Container, unknown>[]
  setColumns: (columns: Column<Container, unknown>[]) => void
}

// Create the context with a default value
const TableContext = createContext<TableContextType | undefined>(undefined)

// Default values
const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 50

// Storage keys
const STORAGE_KEYS = {
  CURRENT_PAGE: 'billing-table-current-page',
  PAGE_SIZE: 'billing-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: ContainerFilters): string => {
  return JSON.stringify({
    search: filters.search || '',
    status: [...filters.status].sort(),
    category: [...filters.category].sort(),
    isReturned: filters.isReturned || '',
    ownerCompanyId: filters.ownerCompanyId || '',
    logisticCompanyId: filters.logisticCompanyId || '',
    transporterCompanyId: filters.transporterCompanyId || '',
    storageCompanyId: filters.storageCompanyId || '',
  })
}

// Provider component that will wrap the table components
export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  // Get filters from the context (compartido con ContainerList)
  const { filters } = useContainerFiltersContext()

  // State for columns
  const [columns, setColumns] = useState<Column<Container, unknown>[]>([])

  // Justo después de obtener filters, fuerza el valor por defecto si está vacío
  useEffect(() => {
    if (!filters.status || filters.status.length === 0) {
      filters.status = ['CREATED']
    }
  }, [filters])

  const { business } = useAppSelector((state) => state.init)

  // Track previous filter state to detect changes
  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  // Initialize state from localStorage
  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )

  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  // Build query parameters from current state and filters
  const queryParams: ContainerQueryParams = {
    page: currentPage,
    per_page: pageSize,
    search:
      filters.search && filters.search.trim() !== '' ? filters.search : null,
    status:
      filters.status &&
      filters.status.length > 0 &&
      !filters.status.includes('all')
        ? filters.status.join(',')
        : null,
    ownerCompanyId:
      filters.ownerCompanyId && filters.ownerCompanyId !== ''
        ? filters.ownerCompanyId
        : null,
    managerCompanyId:
      filters.logisticCompanyId && filters.logisticCompanyId !== ''
        ? filters.logisticCompanyId
        : null,
    transporterCompanyId:
      filters.transporterCompanyId && filters.transporterCompanyId !== ''
        ? filters.transporterCompanyId
        : null,
    storageCompanyId:
      filters.storageCompanyId && filters.storageCompanyId !== ''
        ? filters.storageCompanyId
        : null,
  }

  // Use the invoice query hook with the current parameters
  const {
    data: containerData,
    isLoading,
    isFetching,
    isError,
    isPending,
    isRefetching,
    refetch,
    pagination,
  } = useContainerQuery({
    params: queryParams,
    options: {
      staleTime: 30 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
      // Mantener datos anteriores durante la carga para evitar flickering
      placeholderData: (previousData) => previousData,
    },
    businessId: business?.id ?? 0,
  })

  // Handle page changes with localStorage persistence
  const setCurrentPage = useCallback((page: number) => {
    // Validar que la página sea válida antes de cambiar
    if (page < 1 || isNaN(page)) return

    setCurrentPageState(page)
    storage.set(STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  // Handle page size changes with localStorage persistence
  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(STORAGE_KEYS.PAGE_SIZE, size)

      // Reset to first page when changing page size (if not already on page 1)
      if (currentPage !== INITIAL_PAGE) {
        setCurrentPage(INITIAL_PAGE)
      }
    },
    [currentPage, setCurrentPage]
  )

  // Navigation helpers
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  // Helper to reset to first page (useful for external components)
  const resetToFirstPage = useCallback(() => {
    if (currentPage !== INITIAL_PAGE) {
      setCurrentPage(INITIAL_PAGE)
    }
  }, [currentPage, setCurrentPage])

  // Sync with API response if there's a mismatch (fallback safety)
  // Solo sincronizar si hay datos y la página difiere significativamente
  useEffect(() => {
    if (
      containerData?.currentPage &&
      containerData.currentPage !== currentPage &&
      !isFetching // No sincronizar durante fetching para evitar loops
    ) {
      setCurrentPageState(containerData.currentPage)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, containerData.currentPage)
    }
  }, [containerData?.currentPage, currentPage, isFetching])

  // Aplicar filtros locales a los datos recibidos del backend
  const filteredData = useMemo(() => {
    if (!containerData) return null

    let filteredItems = [...containerData.items]
    let hasLocalFilters = false

    // Filtrar por categoría
    if (
      filters.category &&
      filters.category.length === 1 &&
      !filters.category.includes('all')
    ) {
      hasLocalFilters = true
      const categoryId = filters.category[0]
      filteredItems = filteredItems.filter((container) => {
        // Las categorías son strings en el modelo de datos
        return container.categories.some((category: any) => {
          // Si la categoría es un string, comparamos directamente
          if (typeof category === 'string') {
            return category === categoryId
          }
          // Si la categoría es un objeto, comparamos su id
          return String(category.id) === categoryId
        })
      })
    }

    // Filtrar por isReturned
    if (filters.isReturned !== 'all') {
      hasLocalFilters = true
      const isReturned = filters.isReturned === 'true'
      filteredItems = filteredItems.filter(
        (container) => container.isReturned === isReturned
      )
    }

    //Deje esto porque no se por que se estan realizando filtrados locales en el frontend
    // Si hay filtros locales, recalcular totales basándose en elementos filtrados
    // Si no hay filtros locales, usar los totales originales del servidor
    if (hasLocalFilters) {
      const filteredTotalItems = filteredItems.length
      const filteredTotalPages = Math.ceil(filteredTotalItems / pageSize)

      return {
        ...containerData,
        items: filteredItems,
        totalItems: filteredTotalItems,
        totalPages: filteredTotalPages,
        // Ajustar currentPage si es mayor que el total de páginas filtradas
        currentPage: Math.min(
          containerData.currentPage,
          Math.max(1, filteredTotalPages)
        ),
      }
    } else {
      return {
        ...containerData,
        items: filteredItems,
        // Mantener los totales originales del servidor
        totalItems: containerData.totalItems,
        totalPages: containerData.totalPages,
        currentPage: containerData.currentPage,
      }
    }
  }, [
    containerData,
    filters.category,
    filters.isReturned,
    filters.search,
    pageSize,
  ])

  // Ajustar la página actual si es mayor que el total de páginas filtradas
  useEffect(() => {
    if (
      filteredData &&
      filteredData.totalPages > 0 &&
      currentPage > filteredData.totalPages &&
      !isFetching // Solo ajustar cuando no estamos cargando
    ) {
      const newPage = Math.max(1, filteredData.totalPages)
      setCurrentPageState(newPage)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, newPage)
    }
  }, [filteredData?.totalPages, currentPage, isFetching])

  // Create the context value object
  const contextValue: TableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    containerData: filteredData || null,
    isLoading: isLoading,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    resetToFirstPage,
    columns,
    setColumns,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}

// Export storage utilities for external use if needed
export const tableStorage = {
  clearAll: () => {
    Object.values(STORAGE_KEYS).forEach((key) => storage.remove(key))
  },
  getSavedPage: () => storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE),
  getSavedPageSize: () =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE),
}
